import { Injectable } from '@angular/core';

import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanMatch,
  Data,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';

import {
  firstValueFrom
} from 'rxjs';

import {
  AccessMode,
  TokenDecodeService
} from '../services/token-decode.service';

import {
  Menu,
  MenuService
} from '../services/menu.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard
  implements CanActivate, CanMatch {

  constructor(
    private tokenService: TokenDecodeService,
    private router: Router,
    private menuService: MenuService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {

    return this.checkAccess(
      route.data,
      state.url
    );
  }

  canMatch(
    route: Route,
    segments: UrlSegment[]
  ): Promise<boolean | UrlTree> {

    const requestedUrl =
      '/' +
      segments
        .map(segment => segment.path)
        .join('/');

    return this.checkAccess(
      route.data || {},
      requestedUrl
    );
  }

  private async checkAccess(
    data: Data,
    requestedUrl: string
  ): Promise<boolean | UrlTree> {

    const restored =
      this.tokenService.restore();

    if (!restored) {

      return this.router.createUrlTree(
        ['/login'],
        {
          queryParams: {
            returnUrl: requestedUrl
          }
        }
      );
    }

    const permissions =
      this.toArray<string>(
        data?.['permissions']
      );

    const roles =
      this.toArray<string>(
        data?.['roles']
      );

    const mode =
      (
        data?.['accessMode'] ||
        'any'
      ) as AccessMode;

    const permissionAllowed =
      !permissions.length ||
      this.tokenService.hasPermissions(
        permissions,
        mode
      );

    const roleAllowed =
      !roles.length ||
      (
        mode === 'all'
          ? this.tokenService
              .hasAllAuthorities(
                roles
              )
          : this.tokenService
              .hasAnyAuthority(
                roles
              )
      );

    const allowed =
      permissionAllowed &&
      roleAllowed;

    if (allowed) {
      return true;
    }

    /*
     * Store the actual URL that was denied.
     *
     * Example:
     * /user-management
     * /roles
     * /menu
     */
    sessionStorage.setItem(
      'accessDeniedUrl',
      requestedUrl
    );

    try {

      const menus =
        await firstValueFrom(
          this.menuService
            .loadMyMenus(false)
        );

      const firstRoute =
        this.findFirstAvailableRoute(
          menus,
          requestedUrl
        );

      if (firstRoute) {

        return this.router.parseUrl(
          firstRoute
        );
      }

    } catch (error) {

      console.error(
        'Unable to resolve available menu:',
        error
      );
    }

    /*
     * Profile does not depend on menu permission,
     * so use it as final fallback.
     */
    return this.router.createUrlTree(
      ['/profile']
    );
  }

  private findFirstAvailableRoute(
    menus: Menu[],
    deniedUrl: string
  ): string | null {

    const denied =
      this.normalizeUrl(
        deniedUrl
      );

    for (const menu of menus || []) {

      const route =
        this.normalizeUrl(
          menu.routePath || ''
        );

      /*
       * Do not redirect back to the
       * route that was just denied.
       */
      if (
        route &&
        route !== denied
      ) {
        return menu.routePath!;
      }

      if (menu.subMenus?.length) {

        const childRoute =
          this.findFirstAvailableRoute(
            menu.subMenus,
            deniedUrl
          );

        if (childRoute) {
          return childRoute;
        }
      }
    }

    return null;
  }

  private normalizeUrl(
    url: string
  ): string {

    return String(url || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '');
  }

  private toArray<T>(
    value:
      | T
      | T[]
      | readonly T[]
      | null
      | undefined
  ): T[] {

    if (!value) {
      return [];
    }

    return Array.isArray(value)
      ? [...value] as T[]
      : [value as T];
  }
}